import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Edit3, Save, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button, ErrorState, Panel, Spinner } from '../../components/ui'
import {
  deleteUserSkill,
  fetchUserSkills,
  saveUserSkill,
  updateUserSkill,
  type UserSkill,
} from '../../lib/userSkillsApi'

function fmtDate(value: string | null | undefined): string {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

export function PersonalSkillsPage() {
  return (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-app-bg">
      <PageHeader crumbs={[{ label: 'Skills' }]} />
      <PersonalSkillsTab active />
    </div>
  )
}

export function PersonalSkillsTab({ active }: { active: boolean }) {
  const queryClient = useQueryClient()
  const [skillKey, setSkillKey] = useState('')
  const [title, setTitle] = useState('')
  const [instructions, setInstructions] = useState('')
  const [editingSkill, setEditingSkill] = useState<UserSkill | null>(null)

  const skillsQuery = useQuery({ queryKey: ['user-skills'], queryFn: fetchUserSkills, enabled: active })

  const resetForm = () => {
    setEditingSkill(null)
    setSkillKey('')
    setTitle('')
    setInstructions('')
  }

  const startEdit = (skill: UserSkill) => {
    setEditingSkill(skill)
    setSkillKey(skill.skillKey)
    setTitle(skill.title)
    setInstructions(skill.instructions)
  }

  const saveSkillMutation = useMutation({
    mutationFn: () => (
      editingSkill
        ? updateUserSkill(editingSkill.id, { skillKey, title, instructions })
        : saveUserSkill({ skillKey, title, instructions })
    ),
    onSuccess: () => {
      const wasEditing = editingSkill !== null
      resetForm()
      void queryClient.invalidateQueries({ queryKey: ['user-skills'] })
      toast.success(wasEditing ? 'Skill updated' : 'Skill saved')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't save skill"),
  })

  const deleteSkillMutation = useMutation({
    mutationFn: deleteUserSkill,
    onSuccess: (_data, id) => {
      if (editingSkill?.id === id) resetForm()
      void queryClient.invalidateQueries({ queryKey: ['user-skills'] })
      toast.success('Skill deleted')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't delete skill"),
  })

  const onSaveSkill = (e: FormEvent) => {
    e.preventDefault()
    saveSkillMutation.mutate()
  }

  return (
    <div className="flex flex-col gap-3">
      <Panel title="Personal Skills">
        <form onSubmit={onSaveSkill} className="grid gap-3 md:grid-cols-[180px_minmax(0,1fr)]">
          <input
            value={skillKey}
            onChange={(e) => setSkillKey(e.target.value)}
            placeholder="renewal_summary"
            readOnly={editingSkill !== null}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-slate-400 read-only:bg-gray-50 read-only:text-gray-500"
          />
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Renewal summary"
            className="rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
          />
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Instructions"
            rows={5}
            className="md:col-span-2 rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
          />
          <div className="md:col-span-2 flex justify-end gap-2">
            {editingSkill && (
              <Button type="button" onClick={resetForm}>
                <X size={14} />
                Cancel
              </Button>
            )}
            <Button type="submit" variant="primary" disabled={saveSkillMutation.isPending || !skillKey.trim() || !title.trim() || !instructions.trim()}>
              <Save size={14} />
              {saveSkillMutation.isPending ? 'Saving...' : editingSkill ? 'Update skill' : 'Save skill'}
            </Button>
          </div>
        </form>
        {skillsQuery.isLoading ? (
          <Spinner />
        ) : skillsQuery.isError ? (
          <ErrorState message="Couldn't load skills." />
        ) : (
          <SkillsTable
            rows={skillsQuery.data ?? []}
            onEdit={startEdit}
            onDelete={(id) => deleteSkillMutation.mutate(id)}
            deletingId={deleteSkillMutation.variables ?? null}
          />
        )}
      </Panel>
    </div>
  )
}

function SkillsTable({
  rows,
  onEdit,
  onDelete,
  deletingId,
}: {
  rows: UserSkill[]
  onEdit: (skill: UserSkill) => void
  onDelete: (id: string) => void
  deletingId: string | null
}) {
  if (rows.length === 0) return <div className="mt-4 rounded-md border border-dashed border-gray-200 p-6 text-center text-sm text-text-muted">No skills.</div>
  return (
    <div className="mt-4 overflow-hidden rounded-md border border-gray-200">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-gray-200 bg-gray-50 text-xs font-medium text-text-muted">
          <tr><th className="px-3 py-2">Skill</th><th className="px-3 py-2">Updated</th><th className="px-3 py-2 text-right">Actions</th></tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((skill) => (
            <tr key={skill.id}>
              <td className="px-3 py-2"><div className="font-medium text-gray-900">{skill.title}</div><div className="text-xs text-gray-500">{skill.skillKey}</div></td>
              <td className="px-3 py-2 text-gray-500">{fmtDate(skill.updatedAt)}</td>
              <td className="px-3 py-2 text-right">
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" aria-label="Edit skill" onClick={() => onEdit(skill)}><Edit3 size={14} /></Button>
                  <Button variant="ghost" aria-label="Delete skill" className="!text-red-600" disabled={deletingId === skill.id} onClick={() => onDelete(skill.id)}><Trash2 size={14} /></Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
